//third-party
import { Duration, LocalDateTime, LocalDate, LocalTime, ChronoUnit } from "@js-joda/core";
import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import React from "react";
import { add } from "dinero.js";
import { Placeholder, FormControl, InputGroup, Button as BsButton } from "react-bootstrap";

//spotsync
import { showDate, showDurationShort, showTime, showBrowserDate, showBrowserTime, createLocalDateTime, nearest30 } from "../Data/Date";
import { showMoney, Money, cents, toCents } from "../Data/Money";
import { Api } from "../Data/Api";
import { ReservePreviewResp, ReserveInitReq } from "../Data/ApiTypes";
import { emitReserveInitReq } from "../Data/ApiEmit";
import { Input } from "./Input";
import { Select } from "./Select";
import { Button } from "./Button";
import { Pencil } from "react-bootstrap-icons";
import { ServerRequestsParkingValidationByIdData, ServerRequestsAvailableOptInFee, DataEntitiesFinanceOperatorFeeScope } from "../shared/api/axios-client";
import { OptInFeesSelector, OptInFeesSelection } from "./OptInFeesSelector";
import { ExtendSessionQuery } from "../Data/Atoms";
import { MonthlyPriceBreakdown } from "./MonthlyPriceBreakdown";
import { toast } from "react-toastify";

export interface ReservationDetailsHourlyProps {
    start: LocalDateTime;
    end: LocalDateTime;
    location: string;
    facilityId: number;
    /**Don't show fees  on preview, only on checkout details */
    isCheckout: boolean;
    /**Callback with rate availability status */
    onRateAvailable?: (isAvailable: boolean) => void;
    /**Callback with loading status */
    onLoadingChange?: (isLoading: boolean) => void;
    /**Callback when start time changes */
    onStartChange?: (newStart: LocalDateTime) => void;
    /**Callback when end time changes */
    onEndChange?: (newEnd: LocalDateTime) => void;
    /**Whether times are editable (default: false) */
    editable?: boolean;
    /**Callback when opt-in fees selection changes */
    onOptInFeesChange?: (selection: OptInFeesSelection) => void;
    validationRequest?: ServerRequestsParkingValidationByIdData;
    /**Callback when parkNow changes */
    onParkNowChange?: (isParkNow: boolean) => void;
    /**Callback when parkNowMode changes */
    onParkNowModeChange?: (mode: 'duration' | 'until') => void;
    /**Initial parkNowMode from parent (e.g., from checkout atom) */
    initialParkNowMode?: 'duration' | 'until';
    /**Initial parkNow from parent (e.g., from checkout atom) */
    initialParkNow?: boolean;
    extendMode?: boolean;
    extendSession?: ExtendSessionQuery;
    validationCode?: string;
    onValidationCodeChange?: (code: string) => void;
    /**Show validation code input when facility has active code validations */
    showValidationCode?: boolean;
    /**Checkout preview total is $0 (e.g. validation code covers full cost) */
    onIsFreeCheckoutChange?: (isFree: boolean) => void;
}

function isPreviewFree( preview: ReservePreviewResp ): boolean {
    const total = preview.fees.reduce( ( sum, fee ) => add( sum, cents( fee.amount ) ), preview.price );
    return toCents( total ) === 0;
}

const DEFAULT_PARK_NOW_DURATION_HOURS = 3;

export function ReservationDetailsHourly( props: ReservationDetailsHourlyProps ) {
    const { start, end, location, facilityId, isCheckout, onRateAvailable, onLoadingChange, onStartChange, onEndChange, onOptInFeesChange, editable = false, validationRequest, onParkNowChange, onParkNowModeChange, initialParkNowMode, initialParkNow: initialParkNowProp, extendMode = false, extendSession, validationCode = "", onValidationCodeChange, showValidationCode = false, onIsFreeCheckoutChange } = { ...props };
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [previewData, setPreviewData] = useState<ReservePreviewResp | null>(null);
    const [rateAvailable, setRateAvailable] = useState(false);
    const [optInFeesSelection, setOptInFeesSelection] = useState<OptInFeesSelection>({});
    const [optInFees, setOptInFees] = useState<ServerRequestsAvailableOptInFee[]>([]);
    const [validationCodeInput, setValidationCodeInput] = useState(validationCode);
    const [appliedValidationCode, setAppliedValidationCode] = useState(validationCode);
    const [pendingValidationCode, setPendingValidationCode] = useState("");
    const validationCodeJustApplied = useRef(false);
    
    // Initialize parkNow from URL params, prop, or defaulting based on start time
    const initialParkNow = useMemo(() => {
        if (extendMode) {
            return true;
        }
        // First priority: Check prop from parent (e.g., from checkout atom)
        if (initialParkNowProp !== undefined) {
            return initialParkNowProp;
        }
        
        // Second priority: Check URL parameter (from "Edit Details" navigation)
        const urlParam = searchParams.get('parkNow');
        if (urlParam !== null) {
            return urlParam === 'true';
        }
        
        // Third priority: If start time is more than 15 minutes in the future, default to Park Later mode
        const now = LocalDateTime.now();
        const threshold = now.plusMinutes(15);
        const isStartInFuture = start.isAfter(threshold);
        
        if (isStartInFuture) {
            return false; // Use Park Later mode
        }
        
        // Default to Park Now mode
        return true;
    }, [searchParams, start, initialParkNowProp, extendMode]);
    
    const [parkNow, setParkNow] = useState(initialParkNow);
    const [isLongTerm, setIsLongTerm] = useState(false);
    const durationFromQuery = useMemo(() => {
        const raw = (searchParams.get("duration") ?? "").replace(/\+/g, " ").trim();
        if (!raw) {
            return undefined;
        }
        const parsed = parseFloat(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return undefined;
        }
        return (Math.round(parsed * 2) / 2).toString();
    }, [searchParams]);
    const [hasManualDurationSelection, setHasManualDurationSelection] = useState(false);
    const effectiveDurationFromQuery = hasManualDurationSelection ? undefined : durationFromQuery;

    useEffect(() => {
        // If the URL duration changes (or is removed), allow query-driven duration again.
        setHasManualDurationSelection(false);
    }, [durationFromQuery]);
    
    // Initialize parkNowMode from props, URL params, or default to 'duration'
    const initialParkNowModeResolved = useMemo(() => {
        // First priority: prop from parent (e.g., from checkout atom)
        if (initialParkNowMode) {
            return initialParkNowMode;
        }
        // Second priority: URL parameter
        const urlParam = searchParams.get('parkNowMode');
        if (urlParam === 'until') {
            return 'until';
        }
        return 'duration';
    }, [searchParams, initialParkNowMode]);
    
    const [parkNowMode, setParkNowMode] = useState<'duration' | 'until'>(initialParkNowModeResolved);
    const prevParkNowRef = useRef(parkNow);
    
    // Handle Park Now mode changes - update times and URL
    useEffect(() => {
        const switchedToParkNow = !prevParkNowRef.current && parkNow;
        prevParkNowRef.current = parkNow;

        if (editable && parkNow) {
            // Update URL parameters and notify parent
            const newParams = new URLSearchParams(searchParams);
            newParams.set('parkNowMode', parkNowMode);
            setSearchParams(newParams, { replace: true });
            onParkNowModeChange?.(parkNowMode);
            
            // Check if this is initial load (no URL params) or mode switching (has URL params)
            const hasParkNowParam = searchParams.has('parkNow');
            const hasParkNowModeParam = searchParams.has('parkNowMode');
            const isInitialLoad = !hasParkNowParam && !hasParkNowModeParam;
            const isModeSwitching = hasParkNowParam || hasParkNowModeParam;
            
            const now = LocalDateTime.now();
            
            if (parkNowMode === 'duration') {
                // Duration mode: calculate exact duration and set exact end time
                const defaultHours = effectiveDurationFromQuery
                    ? parseFloat(effectiveDurationFromQuery)
                    : DEFAULT_PARK_NOW_DURATION_HOURS;
                const hours = switchedToParkNow
                    ? defaultHours
                    : effectiveDurationFromQuery
                        ? parseFloat(effectiveDurationFromQuery)
                        : Math.round(Duration.between(start, end).toMinutes() / 60 * 2) / 2;
                const calculatedEnd = now.plusMinutes(hours * 60);
                
                // On initial load, only update if significantly different
                // On mode switching, always update (like handleDurationChange does)
                if (isModeSwitching || Math.abs(Duration.between(end, calculatedEnd).seconds()) > 1) {
                    onStartChange?.(now);
                    onEndChange?.(calculatedEnd);
                }
            } else if (parkNowMode === 'until') {
                // Until mode: round end time to nearest 30-minute interval
                const roundedEnd = nearest30(end);
                
                // On initial load, set to 3 hours from now if not on 30-min boundary
                // On mode switching, always round current end time (like handleParkNowEndTimeChange does)
                if (isInitialLoad) {
                    const defaultEnd = now.plusHours(3);
                    const defaultRounded = nearest30(defaultEnd);
                    const endMinute = end.minute();
                    const isOn30MinBoundary = endMinute === 0 || endMinute === 30;
                    
                    if (!isOn30MinBoundary || Math.abs(Duration.between(end, defaultRounded).toMinutes()) > 30) {
                        onStartChange?.(now);
                        onEndChange?.(defaultRounded);
                    }
                } else if (isModeSwitching) {
                    // Mode switching: round current end time and update start to now
                    onStartChange?.(now);
                    onEndChange?.(roundedEnd);
                }
            }
        }
    }, [parkNow, parkNowMode, editable, effectiveDurationFromQuery]);
    
    const { rateProgramId: rateProgramIdParam } = useParams();
    const { validationRequestId } = useParams();
    const nav = useNavigate();
    const isEvent = rateProgramIdParam;
    const isValidation = validationRequestId != undefined;

    // Helper functions for time options (similar to HourlySearch)
    const getAllTimes = () => {
        const times: Array<LocalTime> = [];
        let time = LocalTime.of(0, 0);
        for (let i = 0; i < 48; i++) {
            times.push(time);
            time = time.plusMinutes(30);
        }
        return times;
    };

    const timeOption = (time: LocalTime) => {
        const str = showBrowserTime(time);
        return <option key={str} value={str}>
            {showTime(time)}
        </option>;
    };

    const getCheckInTimes = (checkInDate: LocalDate) => {
        const now = nearest30(LocalDateTime.now());
        if (now.toLocalDate().isBefore(checkInDate)) {
            return getAllTimes().map(timeOption);
        }
        const allTimes = getAllTimes();
        const nearest = nearest30(now).toLocalTime().truncatedTo(ChronoUnit.MINUTES);
        const filtered = allTimes.filter(t => {
            const expr = t.equals(nearest) || t.isAfter(nearest);
            return expr;
        });
        const timeOptions = filtered.map(timeOption);
        return timeOptions;
    };

    const getCheckOutTimes = (checkInDateTime: LocalDateTime, checkOutDate: LocalDate) => {
        const opts: any = [];
        let time = LocalTime.of(0, 0);
        if (!checkInDateTime.toLocalDate().equals(checkOutDate)) {
            return getAllTimes().map(timeOption);
        }
        time = LocalTime.of(checkInDateTime.hour(), checkInDateTime.minute()).plusMinutes(30);
        while (time.isBefore(LocalTime.of(23, 30))) {
            opts.push(timeOption(time));
            time = time.plusMinutes(30);
        }
        opts.push(timeOption(time));
        return opts;
    };

    // Time change handlers
    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        const newStart = createLocalDateTime(newDate, showBrowserTime(start.toLocalTime()));
        // Round to nearest 30-minute interval in Park Later mode
        const roundedStart = nearest30(newStart);
        onStartChange?.(roundedStart);
    };

    const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newTime = e.target.value;
        const newStart = createLocalDateTime(showBrowserDate(start.toLocalDate()), newTime);
        onStartChange?.(newStart);
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        const newEnd = createLocalDateTime(newDate, showBrowserTime(end.toLocalTime()));
        // Round to nearest 30-minute interval in Park Later mode
        const roundedEnd = nearest30(newEnd);
        onEndChange?.(roundedEnd);
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newTime = e.target.value;
        const newEnd = createLocalDateTime(showBrowserDate(end.toLocalDate()), newTime);
        onEndChange?.(newEnd);
    };

    // Park Now handlers
    const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setHasManualDurationSelection(true);
        const hours = parseFloat(e.target.value);
        const now = LocalDateTime.now();
        const newStart = now;
        const calculatedEnd = now.plusMinutes(hours * 60);
        
        onStartChange?.(newStart);
        onEndChange?.(calculatedEnd);
    };

    const handleParkNowEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const dateTimeStr = e.target.value; // Now in format "YYYY-MM-DD"T"HH:mm:ss"
        const now = LocalDateTime.now();
        
        // Split the datetime string
        const [dateStr, timeStr] = dateTimeStr.split('T');
        
        // Parse the date and time to create end datetime
        const newEnd = createLocalDateTime(dateStr, timeStr);
        
        onStartChange?.(now);
        onEndChange?.(newEnd);
    };

    const getDurationOptions = () => {
        const options: JSX.Element[] = [];
        // 0.5 hours to 24 hours in 0.5 hour increments
        for (let hours = 0.5; hours <= 24; hours += 0.5) {
            const label = hours === 1 ? "1 hour" : hours < 1 ? `${hours * 60} minutes` : `${hours} hours`;
            options.push(
                <option key={hours} value={hours}>
                    {label}
                </option>
            );
        }
        return options;
    };

    const getParkNowEndTimes = () => {
        const now = LocalDateTime.now();
        const opts: JSX.Element[] = [];
        
        // Round to next 30-minute interval for display options
        const nextSlot = nearest30(now.plusMinutes(1));
        const startTime = nextSlot.toLocalTime();
        const endOfDay = LocalTime.of(23, 30);
        
        // Times for today (only if we haven't reached end of day yet)
        const today = now.toLocalDate();
        if (startTime.isBefore(endOfDay) || startTime.equals(endOfDay)) {
            let time = startTime;
            while (time.isBefore(endOfDay) || time.equals(endOfDay)) {
                const timeStr = showBrowserTime(time);
                const dateTimeStr = `${showBrowserDate(today)}T${timeStr}`;
                opts.push(
                    <option key={`today-${timeStr}`} value={dateTimeStr}>
                        {showTime(time)} (Today)
                    </option>
                );
                time = time.plusMinutes(30);
                
                // Safety check: if time wrapped around to next day, break
                if (time.isBefore(startTime)) {
                    break;
                }
            }
        }
        
        // Add midnight and times for tomorrow
        const tomorrow = today.plusDays(1);
        const midnightDateTime = `${showBrowserDate(tomorrow)}T${showBrowserTime(LocalTime.MIDNIGHT)}`;
        opts.push(
            <option key="midnight" value={midnightDateTime}>
                {showTime(LocalTime.MIDNIGHT)} (Tomorrow)
            </option>
        );
        
        // Times for tomorrow from 00:30 up to (and including) the original start time
        let time = LocalTime.of(0, 30);
        const stopTime = startTime;
        while (time.isBefore(stopTime) || time.equals(stopTime)) {
            const timeStr = showBrowserTime(time);
            const dateTimeStr = `${showBrowserDate(tomorrow)}T${timeStr}`;
            opts.push(
                <option key={`tomorrow-${timeStr}`} value={dateTimeStr}>
                    {showTime(time)} (Tomorrow)
                </option>
            );
            const nextTime = time.plusMinutes(30);
            
            // Safety check: if we've wrapped around or passed our stop time after incrementing, break
            if (nextTime.isBefore(time) || (time.equals(stopTime))) {
                break;
            }
            time = nextTime;
        }
        
        return opts;
    };

    const parkNowEndTimeOptions = useMemo(() => getParkNowEndTimes(), [parkNow, parkNowMode]);

    // Opt-in fees handlers
    const handleOptInFeesSelectionChange = (selection: OptInFeesSelection) => {
        setOptInFeesSelection(selection);
        onOptInFeesChange?.(selection);
    };

    const handleOptInFeesLoaded = (fees: ServerRequestsAvailableOptInFee[]) => {
        setOptInFees(fees);
    };

    function handleApplyValidationCode() {
        const code = validationCodeInput.trim().toUpperCase();
        if (!code) return;
        if (code.length < 5) {
            toast.error("Code must be at least 5 characters");
            return;
        }
        setValidationCodeInput(code);
        setPendingValidationCode(code);
        validationCodeJustApplied.current = true;
    }

    function handleResetValidationCode() {
        setValidationCodeInput("");
        setAppliedValidationCode("");
        setPendingValidationCode("");
        onValidationCodeChange?.("");
        toast.info("Validation code removed");
    }

    const validationCodeForPreview = pendingValidationCode || appliedValidationCode;

    const isParkLaterEndInvalid = !parkNow && !extendMode && !end.isAfter(start);

    // Park Later: default checkout to 3h after check-in when checkout is not after check-in
    useEffect(() => {
        if (!editable || parkNow || extendMode || end.isAfter(start)) {
            return;
        }
        onEndChange?.(nearest30(start.plusHours(3)));
    }, [editable, parkNow, extendMode, start, end, onEndChange]);

    // Create a stable key for the effect to prevent unnecessary re-runs
    const effectKey = useMemo(() => {
        const selectionKey = Object.keys(optInFeesSelection).sort().map(k => `${k}:${optInFeesSelection[parseInt(k)]}`).join(',');
        return `${facilityId}-${start.toString()}-${end.toString()}-${rateProgramIdParam || 'null'}-${validationCodeForPreview}-${selectionKey}`;
    }, [facilityId, start.toString(), end.toString(), rateProgramIdParam, validationRequest, validationCodeForPreview, optInFeesSelection]);

    useEffect(() => {
        if (isParkLaterEndInvalid) {
            return;
        }

        const fetchPreview = async () => {
            setIsLoading(true);
            onLoadingChange?.(true);

            // Get selected opt-in fee IDs
            const selectedOptInFeeIds = Object.keys(optInFeesSelection)
                .filter(id => optInFeesSelection[parseInt(id)])
                .map(id => parseInt(id));

            const req: ReserveInitReq = {
                facilityId,
                start,
                end,
                guestEmail: null,
                rateProgramId: isEvent ? parseInt(rateProgramIdParam!) : isValidation ? validationRequest?.rateProgramId! : null,
                vehicle: null,
                spaces: isValidation ? validationRequest!.numberOfSpaces! : null,
                selectedOptInFees: selectedOptInFeeIds.length > 0 ? selectedOptInFeeIds : undefined,
                validationCode: validationCodeForPreview.trim() || undefined,
            };
            const result = await Api.reservationPreview(emitReserveInitReq(req));
           
            if (result.isOk) {
                setPreviewData(result.value);
                setRateAvailable(true);
                onRateAvailable?.(true);
                onIsFreeCheckoutChange?.( isCheckout && isPreviewFree( result.value ) );
                if (validationCodeJustApplied.current && pendingValidationCode) {
                    setAppliedValidationCode(pendingValidationCode);
                    onValidationCodeChange?.(pendingValidationCode);
                    setPendingValidationCode("");
                    toast.success("Validation code applied");
                    validationCodeJustApplied.current = false;
                }
                setTimeout(() => {
                    window.scrollTo({
                        top: document.documentElement.scrollHeight,
                        behavior: 'smooth'
                    });
                }, 100);
            } else {
                const errMsg = typeof result.error === "string" ? result.error : "Could not load price preview";
                if (pendingValidationCode.trim()) {
                    toast.error(errMsg.includes("Validation code") ? errMsg : "Sorry, the validation code was not found");
                    validationCodeJustApplied.current = false;
                    setPendingValidationCode("");
                    setValidationCodeInput("");
                    setIsLoading(false);
                    onLoadingChange?.(false);
                    return;
                }
                if (appliedValidationCode.trim()) {
                    toast.error(errMsg.includes("Validation code") ? errMsg : "Sorry, the validation code was not found");
                    validationCodeJustApplied.current = false;
                    setAppliedValidationCode("");
                    setValidationCodeInput("");
                    onValidationCodeChange?.("");
                    setIsLoading(false);
                    onLoadingChange?.(false);
                    return;
                }
                const mockPreviewData: ReservePreviewResp = {
                    price: cents(0),
                    fees: []
                };
                setPreviewData(mockPreviewData);
                setRateAvailable(false);
                onRateAvailable?.(false);
                onIsFreeCheckoutChange?.(false);
            }
            setIsLoading(false);
            onLoadingChange?.(false);
            }

        fetchPreview();
    }, [effectKey, isParkLaterEndInvalid]);

    const getEditDetailsLink = () => {
        const params = new URLSearchParams({
            parkNow: parkNow.toString(),
            parkNowMode
        });
        if (extendSession) {
            params.set("startTime", extendSession.startTime);
            if (extendSession.licensePlate) {
                params.set("licensePlate", extendSession.licensePlate);
            }
            if (extendSession.licenseState) {
                params.set("licenseState", extendSession.licenseState);
            }
            if (extendSession.duration) {
                params.set("duration", extendSession.duration);
            }
        }
        return `/results/${facilityId}?${params.toString()}`;
    };

    return (
        <>
            {!editable ? (
                // Original grid layout for non-editable mode
                <div className="w-100 gap-3 p-3" style={{ display: "inline-grid", gridTemplateColumns: "auto 1fr 1fr", border: "1px solid #dee2e6", borderRadius: "0.375rem", padding: "0.5rem"}}>
                    
                    {!isEvent && !isValidation && (
                        <div className="text-left" style={{ gridColumn: "1 / 4" }}>
                             <Button variant="outline-secondary" size="sm" className="mb-2 px-3"
                              onClick={() => nav(getEditDetailsLink())}><Pencil className="me-1" style={{ marginTop: "-4px" }} />Edit Details</Button>
                        </div>
                    )}
                    
                    <div className="text-start fw-bold" style={{ gridColumn: "1 / 4" }}>{location}</div>
                    
                    {!isEvent && !isValidation && (        
                        <>
                            <div className="fw-bold">{extendMode ? "Extend By" : "Duration"}</div>
                            <div className="text-start">{showDurationShort( Duration.between( start, end ) )}</div>
                            <div></div>
                        </>
                    )}  

                    <div className="text-start">Check In</div>
                    <div className="text-end">{showDate(start.toLocalDate())}</div>
                    <div className="text-end">{showTime(start.toLocalTime())}</div>
                        
                    <div className="text-start">Check Out</div>
                    <div className="text-end">{showDate( end.toLocalDate() )}</div>
                    <div className="text-end">{showTime( end.toLocalTime() )}</div>

                    {showValidationCode && !isEvent && !isValidation && isCheckout && (
                        <div style={{ gridColumn: "1 / 4" }} className="mt-2 mb-2">
                            <InputGroup>
                                <FormControl
                                    placeholder="Enter validation code"
                                    value={validationCodeInput}
                                    onChange={(e) => setValidationCodeInput(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase())}
                                    disabled={appliedValidationCode !== "" || pendingValidationCode !== ""}
                                />
                                {appliedValidationCode === "" ? (
                                    <BsButton
                                        onClick={handleApplyValidationCode}
                                        variant="primary"
                                        disabled={pendingValidationCode !== "" && isLoading}
                                    >
                                        Apply
                                    </BsButton>
                                ) : (
                                    <BsButton onClick={handleResetValidationCode} variant="secondary">
                                        Reset
                                    </BsButton>
                                )}
                            </InputGroup>
                            {appliedValidationCode !== "" && (
                                <span className="badge bg-success fs-6 mt-2">Code Applied</span>
                            )}
                        </div>
                    )}

                    {isValidation && (
                        <>
                            <div className="text-start">Spaces</div>
                                  <div></div>
                            <div className="text-end">{validationRequest?.numberOfSpaces}</div>
                        </>
                    )}

                    {/* Opt-in fees selector */}
                    <div style={{ gridColumn: "1 / 4" }}>
                        <OptInFeesSelector
                            facilityId={facilityId}
                            feeScope={DataEntitiesFinanceOperatorFeeScope.NUMBER_0} // 0 for hourly parking
                            feePaymentType={0} // Regular payment type
                            onSelectionChange={handleOptInFeesSelectionChange}
                            onFeesLoaded={handleOptInFeesLoaded}
                            isCheckout={isCheckout}
                        />
                                        </div>
                    
                
                    {isLoading ? (
                        <div className="border rounded p-2" style={{ gridColumn: "1 / 4" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                        
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                            
                            </div>
                        </div>
                    ) : previewData && (
                        <div className="fade-in" style={{ gridColumn: "1 / 4" }}>
                            <MonthlyPriceBreakdown
                                price={previewData.price}
                                fees={previewData.fees}
                                unavailable={!rateAvailable}
                            />
                        </div>
                    )}
                </div>
            ) : (
                // Flex layout for editable mode
                <div className="w-auto d-flex flex-column gap-3">
                    <div className="text-start fw-bold">{location}</div>
                    
                    {!isEvent && !isValidation && (        
                        <div className="d-flex  align-items-center">
                            <span className="fw-bold">{extendMode ? "Extend By:  " : "Duration:  "}</span>&nbsp;  
                            <span>{showDurationShort( Duration.between( start, end ) )}</span>
                        </div>
                    )}  

                    {/* Park Now / Park Later Toggle */}
                    {!extendMode && !isEvent && !isValidation && (
                        <div className="d-flex gap-2">
                            <button
                                className={`btn btn-sm ${parkNow ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => {
                                    setParkNow(true);
                                    onParkNowChange?.(true);
                                    setParkNowMode('duration');
                                    onParkNowModeChange?.('duration');
                                    setHasManualDurationSelection(false);
                                    const newParams = new URLSearchParams(searchParams);
                                    newParams.set('parkNow', 'true');
                                    newParams.set('parkNowMode', 'duration');
                                    setSearchParams(newParams, { replace: true });
                                    const now = LocalDateTime.now();
                                    const defaultHours = effectiveDurationFromQuery
                                        ? parseFloat(effectiveDurationFromQuery)
                                        : DEFAULT_PARK_NOW_DURATION_HOURS;
                                    const defaultEnd = nearest30(now.plusMinutes(defaultHours * 60));
                                    onStartChange?.(now);
                                    onEndChange?.(defaultEnd);
                                }}
                                style={{ flex: 1 }}
                            >
                                Park Now
                            </button>
                            <button
                                className={`btn btn-sm ${!parkNow ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => {
                                    setParkNow(false);
                                    onParkNowChange?.(false);
                                    setParkNowMode('duration'); // Reset to default mode
                                    // Update URL to reflect Park Later mode
                                    const newParams = new URLSearchParams(searchParams);
                                    newParams.set('parkNow', 'false');
                                    setSearchParams(newParams, { replace: true });
                                    // Round start and end times to nearest 30-minute interval when switching to Park Later
                                    const roundedStart = nearest30(start);
                                    const roundedEnd = nearest30(end);
                                    onStartChange?.(roundedStart);
                                    onEndChange?.(roundedEnd);
                                }}
                                style={{ flex: 1 }}
                            >
                                {isLongTerm ? 'Park Long Term' : 'Park Later'}
                            </button>
                        </div>
                    )}

                    {parkNow && !isEvent && !isValidation ? (
                        // Park Now Mode
                        <>
                            {/* Duration Selector (Either/Or option A) */}
                            <div 
                                className={`border rounded p-3 ${parkNowMode === 'duration' ? 'border-primary border-1 bg-light' : 'border-secondary'} ${parkNowMode !== 'duration' ? 'opacity-50' : ''}`}
                                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                onClick={() => setParkNowMode('duration')}
                            >
                                <div className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="parkNowMode"
                                        id="parkNowModeDuration"
                                        checked={parkNowMode === 'duration'}
                                        onChange={() => setParkNowMode('duration')}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <label className="form-check-label mb-0 fw-bold" htmlFor="parkNowModeDuration" style={{ cursor: 'pointer' }}>
                                        {extendMode ? "Extend for:" : "Park for:"}
                                    </label>
                                </div>
                                <Select
                                    label=""
                                    onFocus={() => setParkNowMode('duration')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setParkNowMode('duration');
                                    }}
                                    value={(() => {
                                        if (effectiveDurationFromQuery) {
                                            return effectiveDurationFromQuery;
                                        }
                                        // Calculate duration from current time to end time
                                        const now = LocalDateTime.now();
                                        const totalMinutes = Duration.between(now, end).toMinutes();
                                        const hours = totalMinutes / 60;
                                        // Round to nearest 0.5 hours for display only
                                        const rounded = Math.round(hours * 2) / 2;
                                        return Math.max(0.5, rounded).toString();
                                    })()}
                                    onChange={handleDurationChange}
                                    children={getDurationOptions()}
                                    style={{ marginTop: "-20px" }}
                                    className="form-control-sm mb-0"
                                />
                            </div>

                            {/* Obvious OR between the two choices */}
                            <div className="text-center fw-bold text-muted" style={{ fontSize: '1.3rem', marginTop: "-12px" , marginBottom: "-12px"  }}>OR</div>

                            {/* Park Until Time Selector (Either/Or option B) */}
                            <div 
                                className={`border rounded p-3 ${parkNowMode === 'until' ? 'border-primary border-1 bg-light' : 'border-secondary'} ${parkNowMode !== 'until' ? 'opacity-50' : ''}`}
                                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                onClick={() => setParkNowMode('until')}
                            >
                                <div className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="parkNowMode"
                                        id="parkNowModeUntil"
                                        checked={parkNowMode === 'until'}
                                        onChange={() => setParkNowMode('until')}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <label className="form-check-label mb-0 fw-bold" htmlFor="parkNowModeUntil" style={{ cursor: 'pointer' }}>
                                        {extendMode ? "Extend until:" : "Park until:"}
                                    </label>
                                </div>
                                <Select
                                    label=""
                                    onFocus={() => setParkNowMode('until')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setParkNowMode('until');
                                    }}
                                    value={(() => {
                                        // Round end time to nearest 30 minutes for display
                                        const roundedEnd = nearest30(end);
                                        return `${showBrowserDate(roundedEnd.toLocalDate())}T${showBrowserTime(roundedEnd.toLocalTime())}`;
                                    })()}
                                    onChange={handleParkNowEndTimeChange}
                                    children={parkNowEndTimeOptions}
                                    style={{ marginTop: "-20px" }}
                                    className="form-control-sm mb-0"
                                />
                            </div>

                            {/* Need to stay longer link - outside the control boxes */}
                            {!extendMode && (
                                <div className="text-center">
                                    <a 
                                        href="#" 
                                        className="text-decoration-none small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsLongTerm(true);
                                            setParkNow(false);
                                            // Round start and end times to nearest 30-minute interval when switching to Park Later
                                            const roundedStart = nearest30(start);
                                            const roundedEnd = nearest30(end);
                                            onStartChange?.(roundedStart);
                                            onEndChange?.(roundedEnd);
                                        }}
                                    >
                                        Need to stay longer? Click here.
                                    </a>
                                </div>
                            )}
                        </>
                    ) : (
                        // Park Later Mode - Original Interface
                        <>
                            {/* Check In Section */}
                            <div className="d-flex flex-column gap-1">
                                <div className="d-none d-md-block d-flex justify-content-between align-items-center">
                                    <span><b>Check In</b></span>
                                </div>
                                
                                <div className="d-flex flex-column gap-1">
                                    <div className="d-md-none">
                                        <label className="form-label mb-0 fw-bold">Check In</label>
                                    </div>
                                    <div className="d-flex flex-column d-md-flex-row gap-2">
                                        <Input
                                            type="date"
                                            label=""
                                            value={showBrowserDate(start.toLocalDate())}
                                            onChange={handleStartDateChange}
                                            style={{ flex: "1", margin: "-15px 0px -10px 0px" }}
                                            className="form-control-sm"
                                        />
                                        <Select
                                            label=""
                                            value={showBrowserTime(start.toLocalTime())}
                                            onChange={handleStartTimeChange}
                                            children={getCheckInTimes(start.toLocalDate())}
                                            style={{ flex: "1", margin: "-15px 0px -10px 0px" }}
                                            className="form-control-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Check Out Section */}
                            <div className="mt-3 d-flex flex-column gap-1">
                                <div className="d-none d-md-block d-flex justify-content-between align-items-center">
                                    <span><b>Check Out</b></span>
                                </div>
                                
                                <div  className="d-flex flex-column gap-1">
                                    <div className="d-md-none">
                                        <label className="form-label mb-0 fw-bold">Check Out</label>
                                    </div>
                                    <div className="d-flex flex-column d-md-flex-row gap-2">
                                        <Input 
                                            type="date"
                                            label=""
                                            value={showBrowserDate(end.toLocalDate())}
                                            onChange={handleEndDateChange}
                                            style={{ flex: "1", margin: "-15px 0px -10px 0px" }}
                                            className="form-control-sm"
                                        />
                                        <Select
                                            label=""
                                            value={showBrowserTime(end.toLocalTime())}
                                            onChange={handleEndTimeChange}
                                            children={getCheckOutTimes(start, end.toLocalDate())}
                                            style={{ flex: "1", margin: "-15px 0px 10px 0px" }}
                                            className="form-control-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
   
                    {isLoading ? (
                        <div className="border rounded p-2">
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                {isCheckout ? (
                                    <>
                                        <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                        <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                        <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                    </>
                                ) : (
                                    <>
                                        <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : previewData && (
                        isCheckout ? (
                            <div className="fade-in">
                                <MonthlyPriceBreakdown
                                    price={previewData.price}
                                    fees={previewData.fees}
                                    unavailable={!rateAvailable}
                                />
                            </div>
                        ) : (
                        <div className="fade-in border rounded p-2">
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                <div className="text-start fs-5 fw-bold">Price</div>
                                <div className="text-end fw-bold fs-5">{rateAvailable ? showMoney(previewData.price) : "N/A"}</div>
                            </div>
                        </div>
                        )
                    )}
                </div>
            )}
        </>
    );
}